import _ from "lodash";
import React, { memo, useCallback, useState } from "react";
import { createPortal } from "react-dom";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import classNames from "classnames";

import {
  CloseOutlined,
  DownArrowOutlined,
  TwoLinesOutlined,
  ChevronDownOutlined,
} from "@toddle-edu/ds-icons";
import { compose } from "lib/apolloClient";
import { isIdEqual } from "Utils";
import { useI18n } from "Hooks";
import { useDispatch } from "Hooks/useDispatch";
import withPusherBind from "UIComponents/PusherConsumerHoc";
import EmojiPicker from "UIComponents/EmojiPicker";

import {
  CHAT_TYPE,
  NOTIFICATION_TYPES,
  areDatesSameDay,
  areMessagesEqual,
  MESSAGE_TYPE,
  isMessagingRevempEnabled,
  getIsAISummariseEnabled,
} from "MessagingV2/modules/Utils";
import { getDateTimeDifference } from "Utils/DateTimeHelpers";
import {
  toggleReaction,
  deleteMessageInCache,
  getMessagingV2ReduxState,
  markConversationAsRead,
  setShowUnreadMessageButton,
  setShowSummariesNewMessagesWithAi,
  setMediaViewerModalState,
  generateSummaryForNewMessages,
} from "MessagingV2/modules/MessagingModule";
import { getUserId } from "modules/CommonSelectors";
import { withChatOptions } from "MessagingV2/providers/ChatOptionsProvider";
import { onEnterOrSpaceKeyDown } from "Utils/AccessibilityHelpers";

import MessageContainer from "./MessageContainer";
import classes from "./Messages.scss";
import { IconButton } from "@toddle-edu/ds-web";
import { DATE_TIME_CONSTANTS } from "Constants/dateTimeConstants";
import { elevations } from "@toddle-edu/ds-theme";

//this function will tell should given message is seperate or not
const getMessageSeperateState = ({
  message,
  messageToCompare,
  idx,
  type = "PREV",
}) => {
  const { createdBy, createdAt, parentMessage = {} } = message;
  const idxCond = _.isEqual(type, "PREV") ? idx > 0 : idx >= 0;

  const isUIRevempEnabled = isMessagingRevempEnabled();
  const isSameSender =
    idxCond &&
    isIdEqual(_.get(createdBy, "id"), _.get(messageToCompare, "createdBy.id"));

  const isSameDate =
    idxCond && areDatesSameDay(createdAt, messageToCompare?.createdAt);

  const isConsecutiveMessageFromUser = isSameDate && isSameSender;

  const isCompareMessageActivity =
    _.get(messageToCompare, "type") === MESSAGE_TYPE.CHANNEL_ACTIVITY;

  const isInteractionMessage =
    _.get(messageToCompare, "type") === MESSAGE_TYPE.INTERACTION;

  const isCompareMessageSummary =
    _.get(messageToCompare, "type") === MESSAGE_TYPE.CONVERSATION_SUMMARY;

  const timeDifferenceOfMessage =
    getDateTimeDifference({
      date1: createdAt,
      date2: messageToCompare?.createdAt,
      differenceBy: DATE_TIME_CONSTANTS.minutes,
    }) >= 2;

  const parentMessageToComparen = _.isEqual(type, "PREV")
    ? parentMessage
    : _.get(messageToCompare, "parentMessage", undefined);
  return (
    isInteractionMessage ||
    isCompareMessageActivity ||
    isCompareMessageSummary ||
    !isConsecutiveMessageFromUser ||
    (isUIRevempEnabled
      ? timeDifferenceOfMessage || !_.isNil(parentMessageToComparen)
      : false)
  );
};

// This is a fix to prevent the page from scrolling when emoji picker is open
// and also to select any other element on the page(focus trap on emoji picker)
const FocusTrapOverlay = () =>
  createPortal(<div className={classes.preventScrollOverlay} />, document.body);

const Messages = props => {
  const {
    messages,
    chatType,
    toggleReaction,
    onEditMessageClick,
    onRetryMessageClick,
    onReplyMessageClick,
    onParentMessageClick,
    highlightedMessageId,
    isMessageOptionsVisible = true,
    setMediaViewerModalState,
    chatId,
    storedLastReadMessage,
    showUnreadLine = true,
    unreadMessageCount,
    getMessageElementIdCb,
    showUnreadMessageButton,
    showScrollToBottom,
    handleLoadEndMessagePage,
    isUserCreator,
    viewOnlyUsers,
    personalChatReceiverUserId,
  } = props;

  const isUIRevempEnabled = isMessagingRevempEnabled();

  const { t } = useI18n(["common"]);
  const dispatch = useDispatch();

  /**
   * type EmojiPickerState = {
   *   message: MessageV2,
   *   element: HTMLElement,
   *   popoverProps?: ReactTinyPopoverProps,
   * } | null;
   */
  const [mainEmojiPickerState, setMainEmojiPickerState] = useState(null);

  const closeEmojiPicker = () => setMainEmojiPickerState(null);

  const handleEmojiSelect = emoji => {
    const { message } = mainEmojiPickerState;
    toggleReaction({ messageId: message.id, emojiObject: emoji, chatId });
    closeEmojiPicker();
  };

  const openPreviewModal = useCallback(
    ({ attachments, selectedAttachment }) => {
      const activeAttachmentIndex = _.findIndex(attachments, attachment =>
        isIdEqual(attachment.id, selectedAttachment.id)
      );
      setMediaViewerModalState({
        attachments,
        activeAttachmentIndex,
      });
    },
    [setMediaViewerModalState]
  );

  const emojiPickerStyle = isUIRevempEnabled
    ? {
        containerStyle: { top: "8px", left: "-32px" },
      }
    : {};

  //todo show if unreadMessageCount and redline is not visible in screen

  const onMarkConversationAsRead = e => {
    e.stopPropagation();
    dispatch(setShowUnreadMessageButton(false));
    markConversationAsRead({ chatId });
  };

  const onJumpToUnreadMessages = e => {
    e.stopPropagation();
    onParentMessageClick(storedLastReadMessage);
  };

  const onGenerateSummaryClick = () => {
    // to disable showing summaries with new messages with red line
    dispatch(setShowSummariesNewMessagesWithAi(false));
    dispatch(
      generateSummaryForNewMessages({
        chatId,
        previousMessageId: _.get(storedLastReadMessage, "id"),
      })
    );
  };

  const isAISummariseEnabled = getIsAISummariseEnabled();

  const handleJumpToUnreadMessagesKeyDown = e => {
    onEnterOrSpaceKeyDown(e, onJumpToUnreadMessages);
  };
  const handleGenerateSummaryKeyDown = e => {
    onEnterOrSpaceKeyDown(e, onGenerateSummaryClick);
  };
  const handleScrollToBottomKeyDown = e => {
    onEnterOrSpaceKeyDown(e, handleLoadEndMessagePage);
  };

  const messageMap = _.keyBy(messages, data => _.get(data, "node.id"));

  return (
    <>
      {/* Optimized approach: Single emoji picker for all the messages */}
      {!_.isNull(mainEmojiPickerState) ? (
        <>
          <FocusTrapOverlay />
          <EmojiPicker
            isOpen
            onClickOutside={closeEmojiPicker}
            onEmojiSelect={handleEmojiSelect}
            pickerProps={{ native: true, showSkinTones: false }}
            popoverProps={{
              padding: 8,
              align: "center",
              reposition: true,
              positions: ["top", "bottom"],
              ..._.get(mainEmojiPickerState, "popoverProps", {}),
              ...emojiPickerStyle,
              isChildrenRefPassed: true,
              childrenRef: { current: mainEmojiPickerState?.element },
            }}
          />
        </>
      ) : null}

      {showUnreadMessageButton && !!unreadMessageCount ? (
        <div className={classes.newMessageButtonContainer}>
          <div
            className={classes.newMessageButton}
            role="button"
            tabIndex={0}
            onClick={onJumpToUnreadMessages}
            onKeyDown={handleJumpToUnreadMessagesKeyDown}
          >
            <DownArrowOutlined size={"xx-small"} />
            <div
              className={classNames("text-label-3", classes.unreadCountText)}
            >
              {t("common:new_message_with_count", {
                count: unreadMessageCount,
              })}
            </div>
            <IconButton
              variant="plain"
              icon={<CloseOutlined />}
              onClick={onMarkConversationAsRead}
              size="xx-small"
            />
          </div>
          {_.gte(unreadMessageCount, 3) && isAISummariseEnabled ? (
            <>
              <div className={classes.divider} />

              <div
                className={classes.newMessageAiButton}
                role="button"
                tabIndex={0}
                onClick={onGenerateSummaryClick}
                onKeyDown={handleGenerateSummaryKeyDown}
              >
                <TwoLinesOutlined size="xx-small" variant="success" />
                <div
                  className={classNames(
                    "text-label-3",
                    classes.summaryWithAiText
                  )}
                >
                  {t("common:summarise_with_ai")}
                </div>
              </div>
            </>
          ) : null}
        </div>
      ) : null}
      {showScrollToBottom ? (
        <div
          className={classes.scrollToBottomContainer}
          style={elevations["elevation-2B"]}
          onClick={handleLoadEndMessagePage}
          role="button"
          tabIndex={0}
          aria-label={t("common:scroll_to_bottom")}
          onKeyDown={handleScrollToBottomKeyDown}
        >
          <ChevronDownOutlined />
        </div>
      ) : null}
      {_.map(messages, ({ node: message }, idx) => {
        const { id, type, createdAt, createdBy } = message;

        // undefined in the first message
        const previousMessage = _.get(messages, [idx - 1, "node"]);
        // undefined in the last message
        const nextMessage = _.get(messages, [idx + 1, "node"]);

        // For the first message in the list, always return false for both
        const isSameDateAsPreviousMessage =
          idx > 0 && areDatesSameDay(createdAt, previousMessage?.createdAt);

        const isSameSenderAsPreviousMessage =
          idx > 0 &&
          isIdEqual(
            _.get(createdBy, "id"),
            _.get(previousMessage, "createdBy.id")
          );

        const isNextConsecutiveMessage =
          !!nextMessage &&
          areDatesSameDay(createdAt, nextMessage.createdAt) &&
          isIdEqual(_.get(createdBy, "id"), _.get(nextMessage, "createdBy.id"));

        // render unread line above the first unread message
        const isUnreadLineVisible =
          showUnreadLine &&
          !!storedLastReadMessage &&
          previousMessage?.id &&
          isIdEqual(previousMessage?.id, storedLastReadMessage.id) &&
          !isIdEqual(getUserId(), _.get(message, "createdBy.id")); // don't show for self messages

        const isCurrentMessageSeperated = getMessageSeperateState({
          message,
          messageToCompare: previousMessage,
          type: "PREV",
          idx,
        });
        const isNextMessageSeperated = getMessageSeperateState({
          message,
          messageToCompare: nextMessage,
          type: "NEXT",
          idx,
        });

        const parentMessageDetails = _.get(
          messageMap,
          _.get(message, "parentMessage.id"),
          {}
        );

        return (
          <MessageContainer
            key={`${type}-${id}`}
            message={message}
            parentMessageDetails={parentMessageDetails}
            chatType={chatType}
            openPreviewModal={openPreviewModal}
            isNextConsecutiveMessage={isNextConsecutiveMessage}
            isUnreadLineVisible={isUnreadLineVisible}
            isCurrentMessageSeperated={isCurrentMessageSeperated}
            isNextMessageSeperated={isNextMessageSeperated}
            {...(isUnreadLineVisible && {
              unreadMessageCount,
            })}
            isSameDateAsPreviousMessage={isSameDateAsPreviousMessage}
            isSameSenderAsPreviousMessage={isSameSenderAsPreviousMessage}
            setMainEmojiPickerState={setMainEmojiPickerState}
            getMessageElementIdCb={getMessageElementIdCb}
            onEditMessageClick={onEditMessageClick}
            onRetryMessageClick={onRetryMessageClick}
            onReplyMessageClick={onReplyMessageClick}
            onParentMessageClick={onParentMessageClick}
            isMessageHighlighted={isIdEqual(id, highlightedMessageId)}
            isEmojiPickerOpenedForMessage={areMessagesEqual(
              message,
              mainEmojiPickerState?.message
            )}
            isUserCreator={isUserCreator}
            isMessageOptionsVisible={isMessageOptionsVisible}
            viewOnlyUsers={viewOnlyUsers}
            personalChatReceiverUserId={personalChatReceiverUserId}
          />
        );
      })}
    </>
  );
};

Messages.propTypes = {
  messages: PropTypes.arrayOf(
    PropTypes.shape({
      node: PropTypes.object.isRequired,
    })
  ).isRequired,
  chatType: PropTypes.oneOf(_.values(CHAT_TYPE)).isRequired,
  toggleReaction: PropTypes.func.isRequired,
  onEditMessageClick: PropTypes.func.isRequired,
  onRetryMessageClick: PropTypes.func.isRequired,
  onReplyMessageClick: PropTypes.func.isRequired,
  onParentMessageClick: PropTypes.func.isRequired,
  highlightedMessageId: PropTypes.string,
  getMessageElementIdCb: PropTypes.func,
  showScrollToBottom: PropTypes.bool,
  handleLoadEndMessagePage: PropTypes.func,
};

Messages.defaultProps = {
  highlightedMessageId: null,
  showScrollToBottom: false,
};

const MessagesWithCompose = compose(
  withPusherBind(["chatMessageReceived"]),
  memo
)(Messages);

const MessagesContainer = props => {
  const { deleteMessageInCache, ...rest } = props;

  const onPusherEventTriggered = data => {
    // New message has been sent
    const { notification_type: notificationType, message_id: messageId } = data;
    switch (notificationType) {
      case NOTIFICATION_TYPES.CONV_MESSAGE_DELETE:
        deleteMessageInCache({ messageId });
        break;
      default:
        break;
    }
  };

  return (
    <MessagesWithCompose
      {...rest}
      onPusherEventTriggered={onPusherEventTriggered}
    />
  );
};

const STABLE_EMPTY_OBJECT = {};

const mapStateToProps = (state, ownProps) => {
  const chatId = _.get(ownProps, "chatId");
  const messagingReduxState = getMessagingV2ReduxState(state);

  const storedLastReadMessage = _.get(
    messagingReduxState,
    ["lastReadMessageState", chatId],
    STABLE_EMPTY_OBJECT
  );

  const showUnreadMessageButton = _.get(
    messagingReduxState,
    "showUnreadMessageButton",
    false
  );
  return {
    storedLastReadMessage,
    chatId,
    showUnreadMessageButton,
  };
};

const mapDispatchToProps = {
  toggleReaction,
  deleteMessageInCache,
  setMediaViewerModalState,
};

export default compose(
  withChatOptions(),
  connect(mapStateToProps, mapDispatchToProps)
)(MessagesContainer);
